import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { LocaleId } from '@astrally/shared';
import { en, type I18nKey } from './en';
import { hi } from './hi';
import { hinglish } from './hinglish';
import { bn } from './bn';

/**
 * Minimal typed i18n. Keys are compile-time checked against the English
 * dictionary; non-English locales are partial and fall back per-key.
 * Swappable for i18next later without touching call sites (same t(key) shape).
 */

const DICTS: Record<LocaleId, Partial<Record<I18nKey, string>>> = {
  en,
  hi,
  hinglish,
  bn,
};

/** Values substituted into `{placeholders}` in a string. */
export type I18nVars = Record<string, string | number>;

function interpolate(template: string, vars?: I18nVars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    // An unknown placeholder stays literal rather than becoming "undefined",
    // so a missing variable is visible in review instead of shipping silently.
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : whole,
  );
}

interface I18nContextValue {
  locale: LocaleId;
  setLocale: (l: LocaleId) => void;
  t: (key: I18nKey, vars?: I18nVars) => string;
}

const I18nContext = createContext<I18nContextValue>({
  locale: 'en',
  setLocale: () => undefined,
  t: (k, vars) => interpolate(en[k], vars),
});

export function I18nProvider({ children, initial }: { children: ReactNode; initial?: LocaleId }) {
  const [locale, setLocale] = useState<LocaleId>(initial ?? 'en');
  const t = useCallback(
    (key: I18nKey, vars?: I18nVars): string => interpolate(DICTS[locale][key] ?? en[key], vars),
    [locale],
  );
  const value = useMemo(() => ({ locale, setLocale, t }), [locale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  return useContext(I18nContext);
}

export type { I18nKey };
